import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Gift } from "lucide-react";
import InstantWinGame from "@/components/InstantWinGame";

const segLabel = (i, winIdx) => {
  if (i === winIdx) return "VINCI";
  return i % 2 ? "★" : "✦";
};
const PICKED_ANIM = { rotate: [0, -6, 6, -4, 4, 0], scale: 1.1 };
const DIMMED_ANIM = { opacity: 0.35 };
const IDLE_ANIM = {};
const boxAnim = (picked, i) => {
  if (picked === i) return PICKED_ANIM;
  return picked !== null ? DIMMED_ANIM : IDLE_ANIM;
};
const boxColor = (picked, i, result, primary) => {
  if (picked !== i) return primary;
  return result === "win" ? "#10b981" : "#94a3b8";
};

const WRAP_INITIAL = { opacity: 0, y: 20 };
const WRAP_ANIMATE = { opacity: 1, y: 0 };
const TAP_SCALE = { scale: 0.95 };

const Wrap = ({ children, testId }) => (
  <motion.div initial={WRAP_INITIAL} animate={WRAP_ANIMATE} className="rounded-3xl bg-white/95 text-slate-900 p-8 shadow-2xl text-center" data-testid={testId}>
    {children}
  </motion.div>
);

function ScratchGame({ result, onComplete, theme }) {
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = theme.accent_color;
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.font = "bold 22px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("GRATTA QUI", c.width / 2, c.height / 2 + 8);
    ctx.globalCompositeOperation = "destination-out";
    let drawing = false;
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return [((p.clientX - r.left) / r.width) * c.width, ((p.clientY - r.top) / r.height) * c.height];
    };
    const scratch = (e) => {
      if (!drawing) return;
      e.preventDefault();
      const [x, y] = pos(e);
      ctx.beginPath();
      ctx.arc(x, y, 26, 0, Math.PI * 2);
      ctx.fill();
      const d = ctx.getImageData(0, 0, c.width, c.height).data;
      let clear = 0;
      for (let i = 3; i < d.length; i += 4 * 8) if (d[i] === 0) clear++;
      if (clear / (d.length / 32) > 0.55 && !done.current) {
        done.current = true;
        setTimeout(onComplete, 900);
      }
    };
    const start = (e) => { drawing = true; scratch(e); };
    const end = () => { drawing = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", scratch); window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false }); c.addEventListener("touchmove", scratch, { passive: false }); c.addEventListener("touchend", end);
    return () => { window.removeEventListener("mouseup", end); };
  }, [theme.accent_color, onComplete]);
  return (
    <Wrap testId="scratch-game">
      <p className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Gratta con il dito per scoprire l'esito</p>
      <div className="relative mx-auto w-full max-w-sm aspect-[16/9] rounded-2xl overflow-hidden border-4" style={{ borderColor: theme.primary_color }}>
        <div className="absolute inset-0 grid place-items-center font-display font-black text-3xl" style={{ background: result === "win" ? "#dcfce7" : "#f1f5f9", color: result === "win" ? "#047857" : "#475569" }}>
          {result === "win" ? "HAI VINTO!" : "RITENTA"}
        </div>
        <canvas ref={ref} width={480} height={270} className="absolute inset-0 w-full h-full touch-none cursor-pointer" data-testid="scratch-canvas" />
      </div>
      <button data-testid="scratch-skip" onClick={onComplete} className="text-xs text-slate-400 mt-4 underline">Mostra il risultato</button>
    </Wrap>
  );
}

function WheelGame({ result, onComplete, theme }) {
  const [spin, setSpin] = useState(false);
  const segs = 8;
  const colors = [theme.primary_color, theme.accent_color];
  const winIdx = 0;
  const loseIdx = 3;
  const target = result === "win" ? winIdx : loseIdx;
  const finalDeg = 360 * 5 + (360 - (target * (360 / segs) + 360 / segs / 2));
  useEffect(() => {
    const t = setTimeout(() => setSpin(true), 400);
    const t2 = setTimeout(onComplete, 5200);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [onComplete]);
  const grad = Array.from({ length: segs }).map((_, i) => `${colors[i % 2]} ${(i * 100) / segs}% ${((i + 1) * 100) / segs}%`).join(", ");
  return (
    <Wrap testId="wheel-game">
      <p className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">La ruota sta girando…</p>
      <div className="relative mx-auto w-64 h-64">
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[26px] border-l-transparent border-r-transparent" style={{ borderTopColor: "#0f172a" }} />
        <div className="w-full h-full rounded-full border-8 border-slate-900 shadow-2xl relative" style={{ background: `conic-gradient(${grad})`, transform: spin ? `rotate(${finalDeg}deg)` : "rotate(0deg)", transition: spin ? "transform 4.5s cubic-bezier(0.15, 0.85, 0.25, 1)" : "none" }} data-testid="wheel">
          {Array.from({ length: segs }).map((_, i) => (
            <div key={`seg-${i}-of-${segs}`} className="absolute left-1/2 top-1/2 text-white font-black text-xs" style={{ transform: `rotate(${i * (360 / segs) + 360 / segs / 2}deg) translateY(-100px) translateX(-50%)` }}>
              {segLabel(i, winIdx)}
            </div>
          ))}
        </div>
        <div className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-white shadow grid place-items-center"><Gift className="h-6 w-6" style={{ color: theme.primary_color }} /></div>
      </div>
    </Wrap>
  );
}

function BoxesGame({ result, onComplete, theme }) {
  const [picked, setPicked] = useState(null);
  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    setTimeout(onComplete, 1600);
  };
  return (
    <Wrap testId="boxes-game">
      <p className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-5">Scegli una scatola fortunata</p>
      <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
        {[0, 1, 2].map((i) => (
          <motion.button key={i} data-testid={`box-${i}`} whileHover={{ scale: picked === null ? 1.06 : 1 }} whileTap={TAP_SCALE} onClick={() => pick(i)}
            animate={boxAnim(picked, i)}
            className="aspect-square rounded-2xl grid place-items-center shadow-lg" style={{ background: boxColor(picked, i, result, theme.primary_color) }}>
            {picked === i ? <span className="font-black text-white text-lg">{result === "win" ? "🎉" : "✕"}</span> : <Gift className="h-10 w-10 text-white" />}
          </motion.button>
        ))}
      </div>
    </Wrap>
  );
}

export default function GameEngine({ type, result, onComplete, theme, prizes, winPrize, sounds }) {
  if (type === "scratch") return <ScratchGame result={result} onComplete={onComplete} theme={theme} />;
  if (type === "wheel") return <WheelGame result={result} onComplete={onComplete} theme={theme} />;
  if (type === "boxes") return <BoxesGame result={result} onComplete={onComplete} theme={theme} />;
  return <InstantWinGame result={result} onComplete={onComplete} prizes={prizes} winPrize={winPrize} sounds={sounds} />;
}
